import apiClient from '../../shared/api/client';

export interface OrgCreationRequest {
  id: number;
  name: string;
  slug: string | null;
  billing_email: string | null;
  headquarters_address: string | null;
  headquarters_city: string | null;
  headquarters_state: string | null;
  headquarters_zip: string | null;
  headquarters_country: string | null;
  headquarters_phone: string | null;
  reason: string | null;
  rejection_reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  requester: { id: number; full_name: string; email_address: string };
  reviewer: { id: number; full_name: string; email_address: string } | null;
  resulting_organization: { id: number; name: string; slug: string } | null;
}

export interface OrgCreationRequestParams {
  name: string;
  slug?: string;
  billing_email?: string;
  reason?: string;
  headquarters_address?: string;
  headquarters_city?: string;
  headquarters_state?: string;
  headquarters_zip?: string;
  headquarters_country?: string;
  headquarters_phone?: string;
}

export const orgCreationRequestApi = {
  create: async (params: OrgCreationRequestParams): Promise<OrgCreationRequest> => {
    const { data } = await apiClient.post('/organization_creation_requests', params);
    return data;
  },
  mine: async (): Promise<{ total_records: number; result: OrgCreationRequest[] }> => {
    const { data } = await apiClient.get('/organization_creation_requests/mine');
    return data;
  },
};

export interface OrgMember {
  id: number;
  role: string;
  status: string;
  accepted_at: string | null;
  invited_at: string | null;
  user: {
    id: number;
    email_address: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string;
  };
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  billing_email: string | null;
  created_at: string;
  members?: OrgMember[];
}

export const organizationApi = {
  list: async (): Promise<Organization[]> => {
    const { data } = await apiClient.get('/organizations');
    return data.result;
  },

  get: async (id: number | string): Promise<Organization> => {
    const { data } = await apiClient.get(`/organizations/${id}`);
    return data;
  },

  create: async (params: { name: string; billing_email?: string }): Promise<Organization> => {
    const { data } = await apiClient.post('/organizations', params);
    return data;
  },

  update: async (id: number | string, params: { name?: string; billing_email?: string }): Promise<Organization> => {
    const { data } = await apiClient.patch(`/organizations/${id}`, params);
    return data;
  },

  invite: async (
    orgId: number | string,
    params: { email: string; role?: string; course_ids?: number[]; event_ids?: number[] },
  ) => {
    const { data } = await apiClient.post(`/organizations/${orgId}/invitations`, params);
    return data;
  },

  updateMember: async (orgId: number | string, memberId: number, params: { role?: string; first_name?: string; last_name?: string; email_address?: string }): Promise<OrgMember> => {
    const { data } = await apiClient.patch(`/organizations/${orgId}/members/${memberId}`, params);
    return data;
  },

  removeMember: async (orgId: number | string, memberId: number): Promise<{ message: string }> => {
    const { data } = await apiClient.delete(`/organizations/${orgId}/members/${memberId}`);
    return data;
  },

  resendInvitation: async (orgId: number | string, memberId: number): Promise<{ message: string }> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/invitations/${memberId}/resend`);
    return data;
  },

  delete: async (id: number | string): Promise<{ message: string }> => {
    const { data } = await apiClient.delete(`/organizations/${id}`);
    return data;
  },
};

export const billingApi = {
  createPortal: async (orgId: number | string): Promise<{ url: string }> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/billing/portal`);
    return data;
  },
};

export interface MemberEnrollment {
  id: number;
  status: string;
  progress_percentage: number;
  completed_at: string | null;
  purchased_at: string;
  course: { id: number; name: string; division: string };
}

export interface MemberRegistration {
  id: number;
  status: string;
  registered_at: string;
  checked_in_at: string | null;
  cancelled_at: string | null;
  event: { id: number; display_title: string; starts_at: string; city: string | null; format: string };
  course: { id: number; name: string };
}

export interface EmployeeStat {
  id: number;
  email: string;
  full_name: string;
  role: string;
  joined_at: string;
  last_signed_in_at: string | null;
  sign_in_count: number;
  enrollment_count: number;
  completed_enrollment_count: number;
  events_attended_count: number;
  redemption_count: number;
  last_activity_at: string | null;
  enrollments: MemberEnrollment[];
  registrations: MemberRegistration[];
}

export interface ActivityEvent {
  id: string | number;
  kind: string;
  created_at: string;
  actor: { id: number; full_name: string; email: string } | null;
  payload: Record<string, unknown>;
}

export interface AnalyticsResponse {
  total_members: number;
  members: EmployeeStat[];
  recent_activity: ActivityEvent[];
}

export const employeeAnalyticsApi = {
  get: async (orgId: number | string): Promise<AnalyticsResponse> => {
    const { data } = await apiClient.get(`/organizations/${orgId}/employee_analytics`);
    return data;
  },
};

export interface PackageEnrollment {
  id: number;
  status: string;
  progress_percentage: number;
  assigned_at: string | null;
  completed_at: string | null;
  /** When the learner's access ends; null for a course that never expires. */
  expires_at?: string | null;
  user: {
    id: number;
    email_address: string;
    full_name: string;
  };
}

export interface PackageEventRegistration {
  id: number;
  status: string;
  assigned_at: string | null;
  completed_at: string | null;
  event: { id: number; display_title: string; starts_at: string; city: string | null };
  user: { id: number; email_address: string; full_name: string };
}

export interface InviteLinkEvent {
  id: number;
  display_title: string;
  starts_at: string;
  city: string | null;
  format: string;
}

export interface CoursePackage {
  id: number;
  seats_purchased: number;
  seats_assigned: number;
  seats_available: number;
  status: string;
  purchased_at: string | null;
  /** The event date this pool is earmarked to — null for floating course pools. */
  event: InviteLinkEvent | null;
  course: {
    id: number;
    name: string;
    division: string;
    delivery_type: 'event' | 'thinkific' | 'hybrid';
    price_cents: number;
  };
  purchased_by: {
    id: number;
    email_address: string;
    full_name: string;
  };
  enrollments: PackageEnrollment[];
  event_registrations: PackageEventRegistration[];
}

export interface InviteLink {
  id: number;
  token: string;
  kind: 'course' | 'event';
  seat_cap: number;
  seats_used: number;
  seats_remaining: number;
  claimable: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  course: { id: number; name: string; slug: string | null };
  event: InviteLinkEvent | null;
  organization: { id: number; name: string; slug: string };
  created_by: { id: number; full_name: string } | null;
}

export interface PublicInviteLink {
  token: string;
  kind: 'course' | 'event';
  expires_at: string | null;
  seats_remaining: number;
  claimable: boolean;
  revoked: boolean;
  expired: boolean;
  organization: { name: string };
  course: { name: string; slug: string | null };
  event: Omit<InviteLinkEvent, 'id'> | null;
}

export interface StartInviteLinkResponse {
  action: 'signup' | 'login';
  email: string;
}

export interface ClaimInviteLinkResponse {
  already_redeemed: boolean;
  membership_created: boolean;
  enrollment_id: number | null;
  event_registration_id: number | null;
  kind: 'course' | 'event';
  organization: { id: number; name: string; slug: string };
  course: { id: number; name: string; slug: string | null };
  event: { id: number; display_title: string; starts_at: string } | null;
}

export const inviteLinksApi = {
  list: async (orgId: number | string): Promise<InviteLink[]> => {
    const { data } = await apiClient.get(`/organizations/${orgId}/invite_links`);
    return data.result;
  },

  create: async (orgId: number | string, params: {
    course_id?: number;
    course_slug?: string;
    /** Event link: mint a claim link over a purchased, event-earmarked seat pool. */
    course_package_id?: number;
    seat_cap: number;
    expires_at?: string | null;
  }): Promise<InviteLink> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/invite_links`, params);
    return data;
  },

  revoke: async (orgId: number | string, id: number | string): Promise<{ message: string }> => {
    const { data } = await apiClient.delete(`/organizations/${orgId}/invite_links/${id}`);
    return data;
  },

  preview: async (token: string): Promise<PublicInviteLink> => {
    const { data } = await apiClient.get(`/invite_links/${encodeURIComponent(token)}`);
    return data;
  },

  /** Resolves whether this email needs sign-up or login BEFORE we hand the
   *  learner to Auth0, and re-maps an imported row onto its Auth0 identity. */
  start: async (token: string, email: string): Promise<StartInviteLinkResponse> => {
    const { data } = await apiClient.post(`/invite_links/${encodeURIComponent(token)}/start`, { email });
    return data;
  },

  claim: async (token: string): Promise<ClaimInviteLinkResponse> => {
    const { data } = await apiClient.post(`/invite_links/${encodeURIComponent(token)}/claim`);
    return data;
  },
};

export const PENDING_INVITE_LINK_TOKEN_KEY = 'wz.pendingInviteLinkToken';

export const orgBillingApi = {
  courseCheckout: async (orgId: number | string, courseId: number | string, params?: {
    user_id: number;
    discount_code?: string;
  }): Promise<{ url: string }> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/courses/${courseId}/checkout`, params);
    return data;
  },

  groupCheckout: async (orgId: number | string, courseId: number | string, params: {
    seats: number;
    discount_code?: string;
  }): Promise<{ url: string }> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/courses/${courseId}/group_checkout`, params);
    return data;
  },

  // "Specific-date" group package: buy N seats earmarked to one event date.
  // Backend creates a CoursePackage with event_id set on charge.succeeded.
  eventGroupCheckout: async (orgId: number | string, eventId: number | string, params: {
    seats: number;
    discount_code?: string;
    /** Also mint a shareable claim link over the purchased pool on fulfillment. */
    generate_invite_link?: boolean;
  }): Promise<{ url: string }> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/events/${eventId}/group_checkout`, params);
    return data;
  },

  /** Invoice / Purchase Order group purchase — no Stripe redirect. The seat
   *  pool (CoursePackage) is created immediately and an unpaid Invoice is
   *  issued for AR. `mode` picks the URL: course = floating pool, event =
   *  date-earmarked pool. Discount codes are not supported on this path. */
  groupOfflineCheckout: async (
    orgId: number | string,
    mode: 'course' | 'event',
    targetId: number | string,
    params: {
      payment_method: 'invoice' | 'purchase_order';
      seats: number;
      po_number?: string;
      billing_email?: string;
      billing_name?: string;
      billing_phone?: string;
    },
  ): Promise<{
    course_package: { id: number; seats_purchased: number };
    invoice: { id: number; invoice_number: string; po_number: string | null; total_cents: number };
  }> => {
    const base = mode === 'course' ? 'courses' : 'events';
    const { data } = await apiClient.post(
      `/organizations/${orgId}/${base}/${targetId}/group_offline_checkout`,
      params,
    );
    return data;
  },

  coursePackages: async (orgId: number | string): Promise<CoursePackage[]> => {
    const { data } = await apiClient.get(`/organizations/${orgId}/course_packages`);
    return data.result;
  },

  assignSeat: async (orgId: number | string, packageId: number | string, params: {
    user_id: number; event_id?: number;
  }): Promise<{ message: string; kind: string; id: number }> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/course_packages/${packageId}/seat_assignments`, params);
    return data;
  },

  unassignSeat: async (orgId: number | string, packageId: number | string, userId: number): Promise<{ message: string }> => {
    const { data } = await apiClient.delete(`/organizations/${orgId}/course_packages/${packageId}/seat_assignments/${userId}`);
    return data;
  },
};

